import { UserProperties } from "@/types/models/User/v3";

const processUserFeatures = (
  hasFeature: (featureName: string) => boolean,
  features: string[],
): Partial<UserProperties> => {
  const isAuthenticatedUser = !!features.length;
  const isImpersonated = hasFeature("is_impersonated");
  const isLiteUser = hasFeature("is_lite_user");
  const hasBulkClassrooms = hasFeature("has_bulk_classrooms");
  const canSyncClassrooms = hasFeature("can_sync_classrooms");
  const canCreateClassrooms = hasFeature("can_create_classrooms");
  // Can be removed once MCAT is fully retired
  const isStudentInDistrictWithAllAdvisoryContent = hasFeature(
    "in_district_with_all_advisory_content",
  );
  const canViewPowerWords = hasFeature("view_power_words");
  const hasAccessToLite = hasFeature("has_access_to_lite");
  // The difference between this property and activeEssentialsLicense in that
  // this one excludes trial essentials licenses, which is a relevant check for
  // some features.canStartPremiumTrial
  const hasPaidEssentialsLicense = hasFeature("has_paid_essentials_license");
  const canStartPremiumTrial = hasFeature("can_start_premium_trial");
  const hasActivePremiumTrialCampaign = hasFeature(
    "has_active_premium_trial_campaign",
  );
  const hasPLPass = hasFeature("has_pl_pass");
  const hadPremiumTrialCampaignExtensionThisSchoolYear = hasFeature(
    "had_premium_trial_campaign_extension_this_school_year",
  );
  const hasSchoolPocLicense = hasFeature("has_school_poc_license");
  const hadPremiumTrialThisSchoolYear = hasFeature(
    "had_premium_trial_this_school_year",
  );
  const hadSchoolPocLicense = hasFeature("had_school_poc_license");
  const hasActivePremiumTrial = hasFeature("has_active_premium_trial");
  const hasActivePremiumTrialCampaignExtension = hasFeature(
    "has_active_premium_trial_campaign_extension",
  );
  const canPrintPremiumContent = hasFeature("can_print");
  const hasPremiumLicense = hasFeature("has_premium_license");
  const hasNceTrialCampaign = hasFeature("has_nce_trial_campaign");
  const hadPremiumTrialThisYear = hasFeature("had_premium_trial_this_year");
  const canSeeTrialExtensionBanner = hasFeature(
    "can_see_trial_extension_banner",
  );

  // Reporting
  const canAdminViewUsageReport = hasFeature("can_view_usage_report");

  const canAdminMakeChanges = hasFeature("can_make_changes_in_admin_console");
  const canViewTheirLicenses = hasFeature("can_view_their_licenses");
  const canViewAdminConsole = hasFeature("can_view_admin_console");

  // Content Settings
  const canViewContentSettings = hasFeature("can_view_content_settings");
  const canModifyContentSettings = hasFeature("can_modify_content_settings");
  const canViewSearch = hasFeature("can_view_search");

  // Onboarding
  const needsInitialOnboarding = hasFeature("needs_initial_onboarding");
  const needsReturningOnboarding = hasFeature("needs_returning_onboarding");
  const needsOnboarding = hasFeature("needs_onboarding");

  // Admin quick start
  const canAccessImplementationGuide = hasFeature(
    "can_access_implementation_guide",
  );

  // District or School configuration
  const canSeeFormative = hasFeature("can_see_formative");
  const canSeeEverwrite = hasFeature("can_see_everwrite");
  const canSeeAiFeatures = hasFeature("can_see_ai_features");

  // Tracking
  const isNewUser = hasFeature("is_new_user");
  const isFirstWeekUser = hasFeature("is_first_week_user");
  const isFirstMonthUser = hasFeature("is_first_month_user");

  const isSchoolEducator = hasFeature("is_educator");

  const needsAssignmentBuilderOnboarding = hasFeature(
    "needs_assignment_builder_onboarding",
  );

  const isTeacher = hasFeature("is_teacher");
  const isParent = hasFeature("is_parent");
  const isStaff = hasFeature("is_staff");
  const isStudent = hasFeature("is_student");
  const hasTeacherRole = hasFeature("is_teacher");

  const isLiteStudent = isStudent && isLiteUser;
  const activeEssentialsLicense = hasFeature("has_essentials_license");
  const activeELALicense = hasFeature("has_ela_license");
  const activeScienceLicense = hasFeature("has_science_license");
  const activeSSLicense = hasFeature("has_social_studies_license");
  const isFreeUser = hasFeature("is_lite_user");

  const isAuthorizedDistrictAdmin = hasFeature("is_district_admin");
  const isAuthorizedPrincipal = hasFeature("is_principal");

  // is_valid_teacher does not currently exist, we need to add it as a Teacher's feature in the BE
  const isAuthorizedTeacher = hasFeature("is_valid_teacher");

  const isAdmin = hasFeature("can_view_admin_console");

  const hasActiveProductLicense = [
    activeEssentialsLicense,
    activeELALicense,
    activeScienceLicense,
    activeSSLicense,
  ].some((isActive) => isActive);

  const needsPremiumTrialOnboarding = hasFeature(
    "needs_premium_trial_onboarding",
  );

  const isTrialUser =
    hasFeature("hasActivePremiumTrialCampaign") ||
    hasFeature("hasActivePremiumTrialCampaignExtension");

  return {
    hasFeature,
    isAuthenticatedUser,
    isTeacher,
    isParent,
    isStaff,
    isStudent,
    isTrialUser,
    hasTeacherRole,
    isLiteStudent,
    activeEssentialsLicense,
    activeELALicense,
    activeScienceLicense,
    hasActiveProductLicense,
    activeSSLicense,
    isFreeUser,
    isAuthorizedDistrictAdmin,
    isAuthorizedPrincipal,
    isAuthorizedTeacher,
    isAdmin,
    canAccessImplementationGuide,
    canAdminMakeChanges,
    canAdminViewUsageReport,
    canCreateClassrooms,
    canModifyContentSettings,
    canPrintPremiumContent,
    canSeeEverwrite,
    canSeeFormative,
    canSeeAiFeatures,
    canSeeTrialExtensionBanner,
    canStartPremiumTrial,
    canSyncClassrooms,
    canViewAdminConsole,
    canViewContentSettings,
    canViewPowerWords,
    canViewSearch,
    canViewTheirLicenses,
    hadPremiumTrialCampaignExtensionThisSchoolYear,
    hadPremiumTrialThisSchoolYear,
    hadPremiumTrialThisYear,
    hadSchoolPocLicense,
    hasAccessToLite,
    hasActivePremiumTrial,
    hasActivePremiumTrialCampaign,
    hasActivePremiumTrialCampaignExtension,
    hasBulkClassrooms,
    hasNceTrialCampaign,
    hasPaidEssentialsLicense,
    hasPLPass,
    hasPremiumLicense,
    hasSchoolPocLicense,
    isFirstMonthUser,
    isFirstWeekUser,
    isImpersonated,
    isLiteUser,
    isNewUser,
    isSchoolEducator,
    isStudentInDistrictWithAllAdvisoryContent,
    needsAssignmentBuilderOnboarding,
    needsInitialOnboarding,
    needsOnboarding,
    needsReturningOnboarding,
    needsPremiumTrialOnboarding,
  };
};

export default processUserFeatures;
